<template>
  <div
    v-if="isOpen"
    class="fixed inset-0 z-[200] flex items-start justify-center px-4 py-8 overflow-y-auto bg-black/50 backdrop-blur-sm"
  >
    <div class="bg-white rounded-xl shadow-2xl w-full max-w-[22rem] flex flex-col transform transition-all animate-in fade-in zoom-in duration-300 text-left relative overflow-hidden">
      <div class="px-6 py-4 bg-[#0d4169] text-white flex items-center justify-between rounded-t-xl shrink-0">
        <h2 class="text-base font-bold m-0">{{ editDepartment ? 'Edit Department' : 'Add New Department' }}</h2>
        <button @click="closeModal" class="p-1 hover:bg-white/10 rounded-full transition-colors text-white">
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" /></svg>
        </button>
      </div>

      <form @submit.prevent="handleSubmit" class="p-6 flex-1 flex flex-col min-h-0">
        <div class="grid grid-cols-1 gap-y-5 overflow-y-auto pr-1">
          <div class="flex flex-col gap-1.5">
            <label class="text-[0.85rem] font-semibold text-[#1e293b]">Department Name</label>
            <input
              v-model.trim="form.name"
              type="text"
              placeholder="Enter department name"
              class="px-3 py-2 border rounded-md outline-none transition-all text-sm"
              :class="errorMessage ? 'border-red-400 focus:border-red-500' : 'border-[#cbd5e1] focus:border-[#2563eb]'"
              required
            />
            <p v-if="errorMessage" class="text-red-500 text-xs mt-0.5 flex items-center gap-1">
              <svg class="w-3.5 h-3.5 shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M18 10A8 8 0 1 1 2 10a8 8 0 0 1 16 0Zm-8-5a.75.75 0 0 1 .75.75v4.5a.75.75 0 0 1-1.5 0v-4.5A.75.75 0 0 1 10 5Zm0 10a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z" clip-rule="evenodd" /></svg>
              {{ errorMessage }}
            </p>
          </div>

          <div class="flex flex-col gap-1.5">
            <label class="text-[0.85rem] font-semibold text-[#1e293b]">Printer IP Address</label>
            <input
              v-model.trim="form.printerIP"
              type="text"
              placeholder="e.g. 192.168.1.100"
              class="px-3 py-2 border border-[#cbd5e1] rounded-md outline-none focus:border-[#2563eb] transition-all text-sm"
            />
          </div>

          <div v-if="editDepartment" class="flex flex-col gap-1.5">
            <label class="text-[0.85rem] font-semibold text-[#1e293b]">How many copies</label>
            <input
              v-model.number="form.howManyCopies"
              type="number"
              min="1"
              step="1"
              placeholder="1"
              class="px-3 py-2 border border-[#cbd5e1] rounded-md outline-none focus:border-[#2563eb] transition-all text-sm"
            />
            <p class="text-[0.7rem] text-slate-400 m-0">Number of print copies for this department.</p>
          </div>

          <div class="flex flex-col gap-1.5">
            <label class="text-[0.85rem] font-semibold text-[#1e293b]">Department Image (Optional)</label>
            <label 
              for="dept-image-upload" 
              class="flex items-center gap-3 bg-white border border-dashed border-[#cbd5e1] rounded-lg p-2 cursor-pointer hover:border-[#2563eb] transition-all group min-h-[50px]"
              :class="{ 'border-[#2563eb] bg-blue-50/30': uploadingImage }"
            >
              <div v-if="form.image" class="relative shrink-0">
                <img :src="resolveCompanyLogoUrl(form.image)" class="w-10 h-10 object-contain rounded bg-slate-50 border border-slate-100">
                <button @click.prevent="form.image = ''" class="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full w-4 h-4 flex items-center justify-center text-[10px] shadow-sm hover:bg-red-600 transition-colors z-10">✕</button>
              </div>
              <div v-else class="w-10 h-10 rounded bg-slate-50 border border-dashed border-slate-300 flex items-center justify-center text-slate-300 group-hover:text-[#2563eb] group-hover:border-[#2563eb] transition-all shrink-0">
                <svg v-if="!uploadingImage" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path></svg>
                <svg v-else class="w-5 h-5 animate-spin text-[#2563eb]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
              </div>
              <div class="flex-1 leading-tight overflow-hidden">
                <p class="text-[0.75rem] font-bold text-[#0d4169] group-hover:underline truncate">
                  <span v-if="uploadingImage">Uploading...</span>
                  <span v-else>{{ form.image ? 'Change Image' : 'Choose Department Image' }}</span>
                </p>
                <p class="text-[0.65rem] text-slate-400 mt-0.5">PNG, JPG, max 3MB</p>
              </div>
              <input id="dept-image-upload" type="file" class="hidden" accept="image/*" @change="handleImageUpload">
            </label>
          </div>

          <div class="flex flex-col gap-1.5">
            <label class="text-[0.85rem] font-semibold text-[#1e293b]">Remark</label>
            <textarea
              v-model.trim="form.remark"
              rows="2"
              placeholder="Optional remark"
              class="px-3 py-2 border border-[#cbd5e1] rounded-md outline-none focus:border-[#2563eb] transition-all text-sm resize-none"
            ></textarea>
          </div>
        </div>

        <div class="mt-auto pt-5 border-t border-slate-100 flex items-center justify-end gap-3">
          <button type="button" @click="closeModal" class="px-5 py-2 text-sm font-semibold text-slate-500 hover:text-slate-800 transition-colors">
            Cancel
          </button>
          <button
            type="submit"
            :disabled="saving"
            class="min-w-[100px] px-8 py-2 bg-[#0d4169] text-white text-sm font-bold rounded-md shadow-md hover:bg-[#0a3354] transition-all active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed flex justify-center items-center gap-2"
          >
            <div
              v-if="saving"
              class="h-5 w-5 border-2 border-white/30 border-t-transparent rounded-full animate-spin"
            ></div>
            <span v-if="!saving">{{ editDepartment ? 'Update' : 'Save' }}</span>
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import { defineEmits, defineExpose, defineProps, reactive, ref, watch } from 'vue'
import { apiFetch, normalizeUploadValue, resolveCompanyLogoUrl } from '../../utils/api'

const props = defineProps({
  isOpen: Boolean,
  editDepartment: { type: Object, default: null },
  branches: { type: Array, default: () => [] },
})

const emit = defineEmits(['close', 'save', 'update'])

const saving = ref(false)
const uploadingImage = ref(false)
const errorMessage = ref('')

const getInitialForm = () => ({
  id: null,
  name: '',
  remark: '',
  printerIP: '',
  isCentral: false,
  howManyCopies: 1,
  printer_status: 0,
  image: '',
})

const form = reactive(getInitialForm())

function hydrateForm(dept) {
  if (dept) {
    Object.assign(form, {
      id: dept.id,
      name: dept.name || '',
      remark: dept.remark || '',
      printerIP: dept.printerIP || '',
      isCentral: !!(dept.isCentral ?? dept.is_central),
      howManyCopies: Number(dept.how_many_copies ?? dept.howManyCopies ?? 1) || 1,
      printer_status: dept.printer_status !== undefined ? dept.printer_status : 0,
      image: normalizeUploadValue(dept.image),
    })
  } else {
    Object.assign(form, getInitialForm())
  }
  errorMessage.value = ''
}

watch(
  () => props.editDepartment,
  (val) => {
    hydrateForm(val)
  },
  { immediate: true }
)

watch(
  () => props.isOpen,
  (val) => {
    if (val) {
      // Always refresh fields when the modal opens (add = empty, edit = selected dept)
      hydrateForm(props.editDepartment)
      saving.value = false
      uploadingImage.value = false
    } else {
      errorMessage.value = ''
      saving.value = false
      uploadingImage.value = false
    }
  }
)

function closeModal() {
  Object.assign(form, getInitialForm())
  errorMessage.value = ''
  saving.value = false
  uploadingImage.value = false
  emit('close')
}

async function handleImageUpload(event) {
  const file = event.target.files[0]
  if (!file) return

  // 3MB limit
  if (file.size > 3 * 1024 * 1024) {
    alert('File is too large. Max 3MB.')
    event.target.value = ''
    return
  }

  uploadingImage.value = true
  try {
    const formData = new FormData()
    formData.append('logo', file) // Using 'logo' as the generic upload field name if /upload expects it

    const res = await apiFetch('/upload', {
      method: 'POST',
      body: formData
    })

    if (res.ok) {
      const data = await res.json()
      form.image = normalizeUploadValue(data.url)
    } else {
      throw new Error('Upload failed')
    }
  } catch (err) {
    console.error('Error uploading image:', err)
    alert('Failed to upload image.')
  } finally {
    uploadingImage.value = false
    event.target.value = ''
  }
}

async function handleSubmit() {
  errorMessage.value = ''
  saving.value = true

  const payload = {
    id: form.id,
    name: form.name,
    remark: form.remark,
    printerIP: form.printerIP,
    printer_status: form.printer_status,
    image: normalizeUploadValue(form.image),
  }

  if (props.editDepartment) {
    const copies = Number(form.howManyCopies)
    if (!Number.isInteger(copies) || copies < 1) {
      errorMessage.value = 'How many copies must be a positive whole number.'
      saving.value = false
      return
    }
    payload.howManyCopies = copies
  }

  try {
    if (props.editDepartment) {
      await emit('update', payload)
    } else {
      await emit('save', { ...payload, isCentral: false })
    }
  } catch (err) {
    errorMessage.value = err.message || 'An error occurred. Please try again.'
  } finally {
    saving.value = false
  }
}

// Allow parent to set error message
function setError(msg) {
  errorMessage.value = msg
  saving.value = false
}

defineExpose({ setError })
</script>

<style scoped>
.animate-in {
  animation: 0.2s ease-out forwards;
}
.fade-in {
  from { opacity: 0; }
  to { opacity: 1; }
}
.zoom-in {
  from { transform: scale(0.97); }
  to { transform: scale(1); }
}
</style>
